/**
 * Media Upload Utility
 * Provides a reusable function for uploading media files to a configured endpoint
 */

import { capLimitBytes, getUploadLimits } from '@ai-content-hub/shared-config';

/**
 * Get upload endpoint from environment variables
 * Checks VITE_UPLOAD_ENDPOINT, window.__UPLOAD_ENDPOINT__, then VITE_API_URL /
 * VITE_API_BASE_URL / VITE_AI_API_URL (web-content), else relative /api/upload
 */
export function getUploadEndpoint(): string {
    try {
        // Runtime config first (set by configureContentApi when baseUrl from postMessage/URL)
        if (
            typeof window !== 'undefined' &&
            (window as unknown as { __UPLOAD_ENDPOINT__?: string }).__UPLOAD_ENDPOINT__
        ) {
            return (window as unknown as { __UPLOAD_ENDPOINT__: string }).__UPLOAD_ENDPOINT__;
        }
        // Check for Vite env
        if (typeof import.meta !== 'undefined') {
            const env = (import.meta as unknown as { env?: Record<string, string> }).env;
            if (env?.VITE_UPLOAD_ENDPOINT) {
                return env.VITE_UPLOAD_ENDPOINT;
            }
            // Fallback: derive from API URL (web-content uses VITE_AI_API_URL)
            const apiUrl =
                env?.VITE_API_URL || env?.VITE_API_BASE_URL || env?.VITE_AI_API_URL;
            if (apiUrl) {
                const base = apiUrl.replace(/\/+$/, '');
                return base.includes('/api') ? `${base}/upload` : `${base}/api/upload`;
            }
        }
        return '/api/upload';
    } catch {
        return '/api/upload';
    }
}

/**
 * Upload configuration options
 */
export interface UploadConfig {
    /** Upload endpoint URL (optional - defaults to env VITE_UPLOAD_ENDPOINT or /api/upload) */
    endpoint?: string;
    /** Maximum file size in bytes (default: 10MB) */
    maxSize?: number;
    /** Accepted file types (e.g., ['image/*', 'audio/*', 'video/*']) */
    acceptedTypes?: string[];
    /** Custom headers to include in the request */
    headers?: Record<string, string>;
    /** Field name for the file in the form data (default: 'file') */
    fieldName?: string;
}

/**
 * Upload progress information
 */
export interface UploadProgress {
    /** Progress percentage (0-100) */
    percent: number;
    /** Bytes uploaded so far */
    loaded: number;
    /** Total bytes to upload */
    total: number;
}

/**
 * Upload result
 */
export interface UploadResult {
    /** Whether the upload was successful */
    success: boolean;
    /** URL of the uploaded file (if successful) */
    url?: string;
    /** Error message (if failed) */
    error?: string;
    /** Any additional data returned by the server */
    data?: Record<string, unknown>;
}

/**
 * Upload options for a single upload operation
 */
export interface UploadOptions {
    /** Progress callback */
    onProgress?: (progress: UploadProgress) => void;
    /** Abort signal for cancellation */
    signal?: AbortSignal;
}

/**
 * Default upload configuration
 */
export const DEFAULT_UPLOAD_CONFIG: Partial<UploadConfig> = {
    maxSize: 10 * 1024 * 1024, // 10MB
    acceptedTypes: ['image/*', 'audio/*', 'video/*'],
    fieldName: 'file',
};

/**
 * Validate file before upload
 */
export function validateFile(
    file: File,
    config: Partial<UploadConfig> = {}
): { valid: boolean; error?: string } {
    const { acceptedTypes } = { ...DEFAULT_UPLOAD_CONFIG, ...config };
    // EAA-1212: a caller that names no cap gets the 10MB default, lowered to the
    // server's configured media cap when that is smaller. A caller's explicit
    // `maxSize` is its own decision and is left alone.
    const maxSize =
        'maxSize' in config
            ? config.maxSize
            : capLimitBytes(DEFAULT_UPLOAD_CONFIG.maxSize as number, getUploadLimits().mediaMaxSizeMb);

    // Check file size
    if (maxSize && file.size > maxSize) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
        return {
            valid: false,
            error: `File size exceeds maximum allowed size (${maxSizeMB}MB)`,
        };
    }

    // Check file type
    if (acceptedTypes && acceptedTypes.length > 0) {
        const fileType = file.type;
        const isAccepted = acceptedTypes.some((type) => {
            if (type.endsWith('/*')) {
                // Wildcard type (e.g., 'image/*')
                const category = type.replace('/*', '');
                return fileType.startsWith(category);
            }
            return fileType === type;
        });

        if (!isAccepted) {
            return {
                valid: false,
                error: `File type "${fileType}" is not accepted`,
            };
        }
    }

    return { valid: true };
}

/**
 * Upload a media file to the configured endpoint
 *
 * @param file - The file to upload
 * @param config - Upload configuration
 * @param options - Upload options (progress callback, abort signal)
 * @returns Promise resolving to the upload result
 *
 * @example
 * ```typescript
 * const result = await uploadMedia(file, {
 *   endpoint: 'https://api.example.com/upload',
 *   maxSize: 5 * 1024 * 1024, // 5MB
 * }, {
 *   onProgress: (progress) => console.log(`${progress.percent}%`),
 * });
 *
 * if (result.success) {
 *   console.log('Uploaded to:', result.url);
 * } else {
 *   console.error('Upload failed:', result.error);
 * }
 * ```
 */
export async function uploadMedia(
    file: File,
    config: Partial<UploadConfig> = {},
    options: UploadOptions = {}
): Promise<UploadResult> {
    const { onProgress, signal } = options;
    // Get endpoint from config or environment
    const endpoint = config.endpoint || getUploadEndpoint();
    const { headers: configHeaders, fieldName } = { ...DEFAULT_UPLOAD_CONFIG, ...config };
    // Merge with global getter (e.g. for auth - set by embed: window.__UPLOAD_GET_HEADERS__ = () => ({ Authorization: 'Token x' }))
    const win = typeof window !== 'undefined'
        ? (window as unknown as {
            __UPLOAD_GET_HEADERS__?: () => Record<string, string>;
            APP_VERSION?: string;
            CONTENT_TYPE_VERSIONS?: Record<string, string>;
        })
        : undefined;
    const globalHeaders = win?.__UPLOAD_GET_HEADERS__?.() ?? {};

    // Attach version headers for backend logging
    const versionHeaders: Record<string, string> = {};
    if (win?.APP_VERSION) {
        versionHeaders['X-App-Version'] = win.APP_VERSION;
    }
    if (win?.CONTENT_TYPE_VERSIONS) {
        versionHeaders['X-Content-Type-Versions'] = JSON.stringify(win.CONTENT_TYPE_VERSIONS);
    }

    const headers = { ...globalHeaders, ...versionHeaders, ...configHeaders };

    // Validate file
    const validation = validateFile(file, config);
    if (!validation.valid) {
        return {
            success: false,
            error: validation.error,
        };
    }

    // Create form data
    const formData = new FormData();
    formData.append(fieldName || 'file', file);

    try {
        // Use XMLHttpRequest for progress tracking
        return await new Promise<UploadResult>((resolve) => {
            const xhr = new XMLHttpRequest();

            // Handle abort signal
            if (signal) {
                signal.addEventListener('abort', () => {
                    xhr.abort();
                    resolve({
                        success: false,
                        error: 'Upload cancelled',
                    });
                });
            }

            // Track upload progress
            if (onProgress) {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        onProgress({
                            percent: Math.round((event.loaded / event.total) * 100),
                            loaded: event.loaded,
                            total: event.total,
                        });
                    }
                });
            }

            // Handle completion
            xhr.addEventListener('load', () => {
                const ok = xhr.status >= 200 && xhr.status < 300;
                if (ok) {
                    try {
                        const response = JSON.parse(xhr.responseText) as Record<string, unknown>;
                        const inner =
                            response.data && typeof response.data === 'object'
                                ? (response.data as Record<string, unknown>)
                                : response;
                        const url =
                            (typeof inner.url === 'string' && inner.url) ||
                            (typeof response.url === 'string' && response.url) ||
                            (typeof response.fileUrl === 'string' && response.fileUrl) ||
                            undefined;
                        resolve({
                            success: true,
                            url,
                            data: response,
                        });
                    } catch {
                        // If response is not JSON, assume it's the URL directly
                        resolve({
                            success: true,
                            url: xhr.responseText,
                        });
                    }
                } else {
                    let error = `Upload failed with status ${xhr.status}`;
                    try {
                        const response = JSON.parse(xhr.responseText);
                        error = response.error || response.message || error;
                    } catch {
                        // Use default error message
                    }
                    resolve({
                        success: false,
                        error,
                    });
                }
            });

            // Handle errors
            xhr.addEventListener('error', () => {
                resolve({
                    success: false,
                    error: 'Network error occurred during upload',
                });
            });

            xhr.addEventListener('timeout', () => {
                resolve({
                    success: false,
                    error: 'Upload timed out',
                });
            });

            // Open and send request
            xhr.open('POST', endpoint);

            // Set custom headers
            if (headers) {
                Object.entries(headers).forEach(([key, value]) => {
                    xhr.setRequestHeader(key, value);
                });
            }

            xhr.send(formData);
        });
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
    }
}

/**
 * Get the media type category from a file
 */
export function getMediaType(file: File): 'image' | 'audio' | 'video' | 'unknown' {
    const type = file.type;
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('audio/')) return 'audio';
    if (type.startsWith('video/')) return 'video';
    return 'unknown';
}

/**
 * Create a file input and trigger file selection
 */
export function selectFile(
    accept: string = '*/*',
    multiple: boolean = false
): Promise<File | File[] | null> {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.multiple = multiple;
        input.style.display = 'none';

        input.addEventListener('change', () => {
            const files = input.files;
            if (!files || files.length === 0) {
                resolve(null);
            } else if (multiple) {
                resolve(Array.from(files));
            } else {
                resolve(files[0]);
            }
            document.body.removeChild(input);
        });

        input.addEventListener('cancel', () => {
            resolve(null);
            document.body.removeChild(input);
        });

        document.body.appendChild(input);
        input.click();
    });
}
